import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    AfterViewInit,
    inject,
    input,
    output,
    DestroyRef,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';

import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MatOption } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatFormField, MatError, MatSuffix } from '@angular/material/form-field';
import {
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerToggle,
    MatDatepickerToggleIcon,
} from '@angular/material/datepicker';

import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { CookieService } from 'ngx-cookie-service';
import { Step3Service } from '../../step3/step3.service';
import { AppService } from 'src/app/services/app.service';
import { VehicleDataService } from './vehicle-data.service';
import { OuhaService } from 'src/app/services/ouha.service';
import { RestService } from 'src/app/services/rest.service';
import { OtherService } from 'src/app/services/other.service';
import { TrimFcService } from 'src/app/services/trim-fc.service';
import { CookiesService } from 'src/app/www/cookies/cookies.service';
import { optionsNames, VehicleOptionsService } from './vehicle-options.service';
import { AuthenticationService } from 'src/app/services/authentication.service';

import { disallowedCharactersValidators } from 'src/app/validators/disallowedCharacters.validator';

import {
    licensePlateVal,
    clientsVehiclePriceVal,
    engineVolumeVal,
    enginePowerVal,
    numberOfSeatsVal,
    weightVal,
    productionYearVal,
    technicalCertificateNumberVal,
    vinVal,
} from './vehicle.validators';

import { TDate, TDateN } from 'src/app/models/date.model';
import { TsXmlUid } from 'src/app/tsXml/tsXmlUid.model';
import { TsXmlCodelistEntry } from 'src/app/tsXml/tsXmlCodelistEntry.model';
import { TsXmlParAp1Vehicle } from 'src/app/tsXml/tsXmlParAp1Vehicle.model';
import { TsXmlCodelistEntries } from 'src/app/tsXml/tsXmlCodelistEntries.model';

import { TooltipComponent } from '../../../../modules/tooltip/tooltip.component';
import { NumInputComponent } from '../../../../modules/num-input/num-input.component';

import { AppDateAdapter } from 'src/app/services/dateAdapter';

export type VEHICLE_KEY =
    | 'noLicensePlate'
    | 'notRegistered'
    | 'licensePlate'
    | 'vehicleType'
    | 'brand'
    | 'model'
    | 'modelYear'
    | 'engine'
    | 'bodywork'
    | 'fuel'
    | 'engineVolume'
    | 'enginePower'
    | 'numberOfSeats'
    | 'firstRegistrationDate'
    | 'weight'
    | 'vehicleName'
    | 'productionYear'
    | 'vehiclePrice'
    | 'clientsVehiclePrice'
    | 'withoutVAT'
    | 'garaged'
    | 'expectedKm'
    | 'vehicleUse'
    | 'technicalCertificateNumber'
    | 'vIN'
    | 'noTechnicalCertificate';
export enum spzVal {
    have = 0,
    new,
    import,
    never,
}

@Component({
    selector: 'app-vehicle',
    templateUrl: './vehicle.component.html',
    styleUrls: ['./vehicle.component.scss'],
    imports: [
        FormsModule,
        MatAutocompleteModule,
        MatCheckboxModule,
        MatDatepicker,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatDatepickerToggleIcon,
        MatError,
        MatFormField,
        MatInput,
        MatOption,
        MatRadioButton,
        MatRadioGroup,
        MatSelect,
        MatSuffix,
        NgTemplateOutlet,
        NumInputComponent,
        ReactiveFormsModule,
        TooltipComponent,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [{ provide: DateAdapter, useClass: AppDateAdapter }],
})
export class VehicleComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() now: TDate;
    @Input() clientsUserType: string;
    @Input() buVisible: boolean;
    @Input() vehicle: TsXmlParAp1Vehicle;
    @Input() loadedVehicleParams: { vehicleType: string };
    @Input() product: 'AP1' | 'FP1';
    @Input({ required: false }) listOfObserved: VEHICLE_KEY[];
    public municipality = input<{ zipCode: string; city: string; municipalityCode: string }>();
    public sameAsUser = input<boolean>(true);
    readonly doneEvent = output<{
        sameAsUser: boolean;
        vehicleUseTxt: string;
        vehicleValue: any;
    }>();
    readonly rateEvent = output<TsXmlParAp1Vehicle>();
    readonly validityChangedEvent = output<boolean>();
    readonly changedEvent = output<void>();
    readonly justGoOnEvent = output<{
        sameAsUser: boolean;
        vehicleUseTxt: string;
        vehicleValue: any;
        iAmTiredOfIt?: boolean;
    }>();
    readonly policyHolderSameAsVehicleHolderEvent = output<boolean>();
    readonly policyHolderSameAsVehicleOwnerEvent = output<boolean>();
    readonly parameterChangedEvent = output<{
        key: string;
        value: any;
    }>();
    readonly observedChangedEvent = output<{
        key: VEHICLE_KEY;
        value: unknown;
    }>();
    public vehicleChangedEvent = output<void>();
    public zipCodeChangedEvent = output<{
        zipCode: string;
        city: string;
        municipalityCode: string;
    }>();

    public elementId = input<number>();
    private appServ = inject(AppService);
    private authServ = inject(AuthenticationService);
    private cookieServ = inject(CookieService);
    private cookiesServ = inject(CookiesService);
    private dataServ = inject(VehicleDataService);
    private optionsServ = inject(VehicleOptionsService);
    private othersServ = inject(OtherService);
    private ouhaServ = inject(OuhaService);
    private step3Serv = inject(Step3Service);
    private restServ = inject(RestService);
    private destroyRef = inject(DestroyRef);
    protected trimServ = inject(TrimFcService);

    private subs: Subscription[] = [];
    vehicleForm: FormGroup<{
        noLicensePlate: FormControl<boolean>;
        notRegistered: FormControl<boolean>;
        licensePlate: FormControl<string>;
        vehicleType: FormControl<string>;
        brand: FormControl<string>;
        model: FormControl<string>;
        modelYear: FormControl<string>;
        engine: FormControl<string>;
        bodywork: FormControl<string>;
        fuel: FormControl<string>;
        engineVolume: FormControl<number>;
        enginePower: FormControl<number>;
        numberOfSeats: FormControl<number>;
        firstRegistrationDate: FormControl<TDate>;
        weight: FormControl<number>;
        vehicleName: FormControl<string>;
        productionYear: FormControl<TDate>;
        vehiclePrice: FormControl<boolean>;
        clientsVehiclePrice: FormControl<string>;
        withoutVAT: FormControl<boolean>;
        garaged: FormControl<boolean>;
        expectedKm: FormControl<number>;
        vehicleUse: FormControl<string>;
        technicalCertificateNumber: FormControl<string>;
        vIN: FormControl<string>;
        noTechnicalCertificate: FormControl<boolean>;
        contractualService: FormControl<boolean>;
        contractualAuthorizedService: FormControl<boolean>;
    }>;
    _htmlData: { name: string; title: string; type: string; extras?: any }[];
    tooltips: {
        [key: string]: {
            headline: string;
            text: string;
        };
    };
    spz: FormControl<0 | 1 | 2 | 3>;
    sameAsUserFc: FormControl = new FormControl(true);

    options: {
        [key: string]: { text: string; value: any; valid?: boolean }[];
    };
    minDate: TDate;
    maxDate: TDate;

    visibilities: { [key: string]: boolean };
    markTouchedBuVisible: boolean;
    hideAllAfterSPZ: boolean;
    private _defaultProtections = [
        'vehicleType',
        'technicalCertificateNumber',
        'vIN',
        'contractualService',
        'contractualAuthorizedService',
        'modelYear',
        'bodywork',
        'numberOfSeats',
    ];
    protected theseAreUnderMyProtection = [...this._defaultProtections];
    valid: boolean;
    // finishHimBuVisible: boolean;

    private uid: TsXmlUid;
    private iDareYouIDoubleDareYouMtfk: boolean;
    protected sameAsUserText: string = '';
    protected isElectro: boolean;
    protected zipCodeCity: FormControl<{
        zipCode: string;
        city: string;
        municipalityCode: string;
    }> = new FormControl();
    protected zips: { zipCode: string; city: string; municipalityCode: string }[] = [];
    protected isLoadingZip: boolean;
    private lastSPZLoaded: string = null;
    private oldModel: string;
    private allowedForWithoutRegistration: string[] = [];
    private pendingAllowedPromiseDone = false;
    private pendingAllowedPromise: Promise<any>;


    ngOnInit(): void {
        const same = typeof this.sameAsUser() === 'boolean' ? this.sameAsUser() : true;
        this.sameAsUserFc.setValue(same);
        this.initFP1();
        this.isElectro = ['EV', 'EK'].includes(this.dataServ.savedData?.vehicleValue?.vehicleType);
        this.uid = this.authServ.currentUserUidValue;
        this.setAllowed();
        this.initialize();
        this.setFormGroup();
        this.options = this.optionsServ.options;
        this.loadData();
        this.setVisibFromVehicle(this.vehicle);
        this.initializeSubs();
        if (this.product === 'FP1') {
            this.optionsServ.prepareVehicleWeightData(this.uid).then(() => {
                this.vehicleForm.controls.weight.updateValueAndValidity();
                if (this.vehicleForm.controls.weight.invalid) this.vehicleForm.controls.weight.markAsTouched();
            }).catch(() => {
                this.ouhaServ.ouha({ message: $localize`Nepodařilo se načíst povolené hmotnosti vozidel. Zkuste to prosím znovu.` });
            });
        }
        if (!this.vehicleForm.valid) this.markTouchedBuVisible = true;
    }

    ngAfterViewInit() {
        if (this.vehicleForm.valid) {
            this.prepareData(true).then((data) => {
                this.justGoOnEvent.emit({ ...data, iAmTiredOfIt: true });
            });
        }
    }

    private async initFP1(): Promise<void> {
        if (this.product !== 'FP1') return;
        this.theseAreUnderMyProtection.push('garaged', 'withoutVAT');

        this.zipCodeCity = new FormControl(null, Validators.required);
        if (this.municipality()) {
            setTimeout(() => {
                this.zipCodeCity.setValue(this.municipality());
            });
        }
        const sub = this.zipCodeCity.valueChanges.subscribe((val) =>
            this.zipCodeChangedEvent.emit(val),
        );
        this.destroyRef.onDestroy(() => sub.unsubscribe());
    }

    private initialize() {
        this.hideAllAfterSPZ = !this.dataServ.savedData && this.isVehicleEmpty(this.vehicle);
        this.minDate = new TDate(new Date('1900,1,1'));
        this.maxDate = this.now;
        this._htmlData = this.dataServ.vehicleHtmlData.filter(
            (it) => this.product !== 'FP1' || it.name !== 'expectedKm',
        );
        this.tooltips = this.dataServ.tooltips;
        if (!this.vehicle) this.vehicle = new TsXmlParAp1Vehicle();
    }

    private setAllowed() {
        this.pendingAllowedPromise = this.restServ.parCodelistsEntriesGet('5322', this.uid).then(cd => {
            const ar = cd.entry.map(it => {
                const vehicleType = it.gclColumn.find(itm => itm.columnCode === 'KOD').stringValue;
                const allowed = it.gclColumn.find(itm => itm.columnCode === 'POVOLENA').booleanValue;
                return allowed ? vehicleType : null;
            }).filter(it => !!it);
            this.allowedForWithoutRegistration = ar;
        }).finally(() => this.pendingAllowedPromiseDone = true);
    }

    private isVehicleEmpty(vehicle: TsXmlParAp1Vehicle): boolean {
        return (
            !vehicle ||
            typeof vehicle !== 'object' ||
            !Object.values(vehicle).filter((it) => !!it).length
        );
        // (Object.keys(vehicle).length === 2 && vehicle.vehicleUse === '10' && vehicle.vehicleType === 'OA');
    }

    private setFormGroup() {
        this.spz = new FormControl(this.getSpzVal());
        const hasVehicleInInput = Object.values(this.vehicle).filter((it) => !!it).length;
        const vehicleTypeFC = new FormControl(
            this.getDfVal('vehicleType') || this.loadedVehicleParams?.vehicleType || 'OA',
        );
        this.vehicleForm = new FormGroup(
            {
                noLicensePlate: new FormControl(this.getDfVal('noLicensePlate')),
                notRegistered: new FormControl(this.getDfVal('notRegistered')),
                licensePlate: new FormControl(this.getDfVal('licensePlate'), licensePlateVal),
                vehicleType: vehicleTypeFC,
                withoutRegistration: new FormControl(this.getDfVal('withoutRegistration')),
                brand: new FormControl(this.getDfVal('brand')),
                model: new FormControl(this.getDfVal('model')),
                modelYear: new FormControl(
                    this.getDfVal('modelYear') ||
                    (this.product === 'FP1' ? 'Ostatní' : hasVehicleInInput ? 'Ostatní' : null),
                ),
                engine: new FormControl(this.getDfVal('engine')),
                bodywork: new FormControl(this.getDfVal('bodywork')),
                fuel: new FormControl(this.getDfVal('fuel')),
                engineVolume: new FormControl(this.getDfVal('engineVolume'), engineVolumeVal),
                enginePower: new FormControl(this.getDfVal('enginePower'), enginePowerVal),
                numberOfSeats: new FormControl(this.getDfVal('numberOfSeats'), numberOfSeatsVal),
                firstRegistrationDate: new FormControl(this.getDfVal('firstRegistrationDate')),
                weight: new FormControl(this.getDfVal('weight'), this.product === 'FP1'
                    ? control => this.optionsServ.validateWeight(vehicleTypeFC.value, control.value)
                    : weightVal(vehicleTypeFC)),
                vehicleName: new FormControl(this.getDfVal('vehicleName')),
                productionYear: new FormControl(this.getDfVal('productionYear'), [
                    productionYearVal(this.now),
                    disallowedCharactersValidators.disallowedCharacters(/[^\d]/),
                ]),
                vehiclePrice: new FormControl(this.getDfVal('vehiclePrice')),
                clientsVehiclePrice: new FormControl(
                    this.getDfVal('clientsVehiclePrice'),
                    clientsVehiclePriceVal,
                ),
                withoutVAT: new FormControl(this.getDfVal('withoutVAT')),
                garaged: new FormControl(this.getDfVal('garaged')),
                expectedKm: new FormControl(this.getDfVal('expectedKm')),
                vehicleUse: new FormControl(
                    this.getDfVal('vehicleUse') === '12' ? null : this.getDfVal('vehicleUse'),
                ),
                technicalCertificateNumber: new FormControl(
                    {
                        value: this.getDfVal('technicalCertificateNumber'),
                        disabled: this.getDfVal('noTechnicalCertificate'),
                    },
                    this.product === 'FP1' ? technicalCertificateNumberVal : null,
                ),
                vIN: new FormControl(this.getDfVal('vIN'), this.product === 'FP1' ? vinVal : null),
                noTechnicalCertificate: new FormControl(this.getDfVal('noTechnicalCertificate')),
                contractualService: new FormControl(this.vehicle?.contractualService || null),
                contractualAuthorizedService: new FormControl(
                    this.vehicle?.contractualAuthorizedService || null,
                ),
            },
            { updateOn: 'blur' },
        ) as any;
        this.setVisibilities();
        this.sameAsUserText = ['EV', 'EK'].includes(this.vehicleForm.controls.vehicleType.value)
            ? $localize`Vlastník vozidla`
            : $localize`Provozovatel vozidla`;
    }

    private getSpzVal(vehicle: TsXmlParAp1Vehicle = this.vehicle): 0 | 1 | 2 | 3 {
        if (!vehicle) return 0;
        if (!vehicle.noLicensePlate && !vehicle.notRegistered) return 0;
        if (vehicle.noLicensePlate && vehicle.notRegistered) return 1;
        if (vehicle.noLicensePlate && !vehicle.notRegistered) return 2;
    }

    private getDfVal(key: string): any {
        let val = this.vehicle[key] || this.dataServ.defaultData[key];
        if (key === 'licensePlate') this.lastSPZLoaded = this.vehicle.licensePlate;
        if (key === 'productionYear' && this.vehicle.productionYear)
            val = this.vehicle.productionYear.getFullYear();
        if (key === 'expectedKm' && this.product === 'FP1') return -1;
        if (key === 'withoutRegistration' && this.product !== 'FP1')
            return this.vehicle.withoutRegistration;
        return val;
    }

    private loadData() {
        if (this.product !== 'AP1') return;
        const ds = this.dataServ.savedData;

        if (!ds) return;
        this.spz.setValue(ds.spz);
        this.sameAsUserFc.setValue(ds.sameAsUser);
        this.theseAreUnderMyProtection = ds.theseAreUnderMyProtection;
        Object.entries(this.vehicleForm.controls).forEach((dt) => {
            const fc = dt[1];
            const name = dt[0];
            if (name in ds.vehicleValue) {
                if (name === 'productionYear' && ds.vehicleValue[name]) {
                    const val = ds.vehicleValue[name];
                    const productionYear: any = val instanceof Date ? val.getFullYear() : val;
                    fc.setValue(productionYear as never);
                } else {
                    if (name === 'licensePlate') this.lastSPZLoaded = ds.vehicleValue[name];
                    fc.setValue(ds.vehicleValue[name] as never);
                }
            }
        });
        if (!ds.vehicleValue.modelYear && ds.vehicleValue.fuel)
            this.vehicleForm.get('modelYear').setValue('Ostatní', { emitEvent: false });
        if (
            this.vehicleForm.get('modelYear').value !== 'Ostatní' &&
            !ds.vehicleValue.engine &&
            ds.vehicleValue.enginePower
        )
            this.vehicleForm.get('engine').setValue('Ostatní', { emitEvent: false });
        if (!ds.vehicleValue.bodywork && ds.vehicleValue.numberOfSeats)
            this.vehicleForm.get('bodywork').setValue('Ostatní', { emitEvent: false });

        this.setVisibilities();
        this.validityChangedEvent.emit(true);
    }

    private setVisibilities() {
        if (!this.visibilities) this.visibilities = {};
        this._htmlData.forEach((dt) => {
            const visibleNew = this.isFCVisible(dt.name);
            const visibleOld = this.visibilities[dt.name];
            if (
                visibleNew &&
                [
                    'spz',
                    'vehicleType',
                    'brand',
                    'model',
                    'modelYear',
                    'engine',
                    'bodywork',
                    'fuel',
                    'expectedKm',
                    'vehicleUse',
                ].includes(dt.name)
            ) {
                this.updateOptionsFor(dt.name);
            }
            if (visibleNew !== visibleOld) {
                this.visibilities[dt.name] = visibleNew;
            }
            setTimeout(() => {
                if (visibleOld && !visibleNew) {
                    this.vehicleForm.get(dt.name).setErrors(null);
                    if (
                        !(
                            this.product === 'AP1'
                                ? this.theseAreUnderMyProtection
                                : [...this.theseAreUnderMyProtection, 'clientsVehiclePrice']
                        ).includes(dt.name) &&
                        this.vehicleForm.get(dt.name).value
                    ) {
                        this.vehicleForm.get(dt.name).setValue(null, { emitEvent: false });
                    }
                }
            });
        });
    }

    private updateOptionsFor(fc: string) {
        if (fc === 'brand') {
            const vehicleType = this.vehicleForm.get('vehicleType').value;
            this.optionsServ.setOptionsFor('brand', this.uid, { vehicleType });
        } else {
            const searchValues = this.getSearchValues(fc);
            this.optionsServ.setOptionsFor(fc as optionsNames, this.uid, searchValues);
        }
    }

    private getSearchValues(fc: string) {
        if (fc === 'expectedKm') {
            const vehicleType = this.vehicleForm.get('vehicleType').value;
            const userPartyType = ['OSVC', 'PO'].includes(this.clientsUserType) ? 'IC' : 'RC';
            return { vehicleType, userPartyType };
        }

        const list = ['brand', 'model', 'modelYear', 'engine', 'bodywork'];
        if (!list.includes(fc)) return null;
        const arr = list.slice(0, list.indexOf(fc));
        const output = {};
        arr.forEach((key) => {
            output[key] = this.vehicleForm.get(key).value;
        });
        return output;
    }

    private isFCVisible(fc: string): boolean {
        let visible = false;
        const modelVisible =
            this.vl('vehicleType') === 'OA' &&
            !!this.vl('brand') &&
            !['OSTATNE', 'NEUVEDENA'].includes(this.vl('brand'));
        switch (fc) {
            case 'spz':
                // visible = this.product !== 'FP1';
                visible = true;
                break;
            case 'licensePlate':
                visible = this.spz.value === spzVal.have;
                break;
            case 'vehicleType':
                // visible = this.product !== 'FP1' && (!!this.spz.value || this.spz.value === 0);
                visible = !!this.spz.value || this.spz.value === 0;
                break;
            case 'brand':
                visible = this.vl('vehicleType') === 'OA';
                break;
            case 'model':
                visible = modelVisible && (!this.options?.model || !!this.options?.model?.length);
                break;
            case 'modelYear':
                visible = false; // this.product !== 'FP1' && (modelVisible && (!this.options?.modelYear || !!this.options?.modelYear?.length));
                break;
            case 'engine':
                visible = false; //modelVisible && !!this.vl('model') && (!this.options?.engine || !!this.options?.engine?.length);
                break;
            case 'bodywork':
                visible = false;
                // modelVisible && !!this.vl('model') &&
                //   (!!this.vl('engine') && this.vl('engine') !== 'Ostatní') &&
                //   (!this.options?.bodywork || !!this.options?.bodywork?.length);
                break;
            case 'fuel':
                visible = !['PV', 'PN', 'EV', 'EK'].includes(this.vl('vehicleType'));
                break;
            case 'engineVolume':
                visible =
                    (!this.vl('engine') || this.vl('engine') === 'Ostatní') &&
                    !['PV', 'PN', 'EV', 'EK'].includes(this.vl('vehicleType')) &&
                    this.vl('fuel') !== 'EP';
                break;
            case 'enginePower':
                visible =
                    (!this.vl('engine') || this.vl('engine') === 'Ostatní') &&
                    !['PV', 'PN'].includes(this.vl('vehicleType'));
                break;
            case 'numberOfSeats':
                visible = false;
                // this.product !== 'FP1' && (!this.vl('bodywork') || this.vl('bodywork') === 'Ostatní') &&
                //   !['PV', 'PN', 'EV', 'EK'].includes(this.vl('vehicleType'));
                break;
            case 'weight':
                visible = !!this.spz.value || this.spz.value === 0;
                break;
            case 'firstRegistrationDate':
                visible = this.spz.value === spzVal.have;
                break;
            case 'vehicleName':
                visible =
                    !['OA'].includes(this.vl('vehicleType')) ||
                    !this.vl('brand') ||
                    !this.vl('model') ||
                    ['579', '529'].includes(this.vl('model')) ||
                    (this.options?.model?.length &&
                        this.options.model.find((br) => br.value === this.vl('model'))?.text ===
                        'Ostatní');
                break;
            case 'productionYear':
                visible = this.spz.value === spzVal.never || this.spz.value === spzVal.import;
                break;
            case 'clientsVehiclePrice':
                const _240Mnth = this.othersServ.getDateChangedBy(this.now, 0, -240);
                let age = this.vl('productionYear') || this.vl('firstRegistrationDate');
                if (typeof age === 'number') age = new Date(age + '');
                else if (typeof age === 'string') age = new Date(age);
                const tooOld = age && age.getTime() < _240Mnth.getTime();
                visible =
                    !tooOld &&
                    !this.vl('vehiclePrice') &&
                    !['EV', 'EK'].includes(this.vl('vehicleType'));
                visible = this.product === 'FP1' ? true : visible;
                break;
            case 'withoutVAT':
                visible = ['PO', 'OSVC'].includes(this.clientsUserType);
                break;
            case 'garaged':
                visible =
                    (!!this.spz.value || this.spz.value === 0) &&
                    !['EV', 'EK'].includes(this.vl('vehicleType'));
                break;
            case 'expectedKm':
                visible = !['PV', 'SPS', 'VZV', 'TRA', 'PN', 'EV', 'EK'].includes(
                    this.vl('vehicleType'),
                );
                break;
            case 'vehicleUse':
                visible = !!this.spz.value || this.spz.value === 0;
                break;
            case 'withoutRegistration':
                visible =
                    this.product !== 'FP1' &&
                    this.allowedForWithoutRegistration.includes(this.vl('vehicleType'));
                break;
        }
        if (!['spz', 'licensePlate'].includes(fc)) {
            visible = visible && !this.hideAllAfterSPZ;
        }
        return visible;
    }

    private setVisibFromVehicle(vehicle: TsXmlParAp1Vehicle): void {
        if (!Object.values(vehicle || {}).filter((it) => !!it).length) {
            this.setVisibilities();
            return;
        }
        if (vehicle.licensePlate && !vehicle.vehicleType) vehicle.vehicleType = 'OA';
        const spz = this.getSpzVal(vehicle) as any;
        const modelVisible = vehicle.vehicleType === 'OA' && vehicle.brand &&
            !['OSTATNE', 'NEUVEDENA'].includes(vehicle.brand);
        const _240Mnth = this.othersServ.getDateChangedBy(this.now, 0, -240);
        let age = this.vl('productionYear') || this.vl('firstRegistrationDate');
        if (typeof age === 'number') age = new Date(age + '');
        else if (typeof age === 'string') age = new Date(age);
        const tooOld = age && age.getTime() < _240Mnth.getTime();
        let clientsVehiclePrice =
            !tooOld && !this.vl('vehiclePrice') && !['EV', 'EK'].includes(this.vl('vehicleType'));
        clientsVehiclePrice = this.product === 'FP1' ? true : clientsVehiclePrice;

        this.visibilities = {
            spz: true,
            licensePlate: spz === spzVal.have,
            vehicleType: !!spz || spz === 0,
            brand: !vehicle.vehicleType || vehicle.vehicleType === 'OA',
            model: modelVisible,
            modelYear: false,
            engine: false, // modelVisible,
            bodywork: false, // modelVisible,
            fuel: !['PV', 'PN', 'EV', 'EK'].includes(vehicle.vehicleType),
            // (!vehicle.engine || vehicle.engine === 'Ostatní') &&
            engineVolume:
                !['PV', 'PN', 'EV', 'EK'].includes(vehicle.vehicleType) && vehicle.fuel !== 'EP',
            // (!vehicle.engine || vehicle.engine === 'Ostatní') &&
            enginePower: !['PV', 'PN'].includes(vehicle.vehicleType),
            numberOfSeats: false,
                // this.product !== 'FP1' &&
                // (!vehicle.bodywork || vehicle.bodywork === 'Ostatní') &&
                // !['PV', 'PN', 'EV', 'EK'].includes(vehicle.vehicleType),
            weight: !!spz || spz === 0,
            firstRegistrationDate: spz === spzVal.have,
            vehicleName:
                !['OA'].includes(vehicle.vehicleType) ||
                !vehicle.brand ||
                !vehicle.model ||
                ['579', '529'].includes(vehicle.model),
            productionYear: spz === spzVal.never || spz === spzVal.import,
            clientsVehiclePrice,
            withoutVAT: ['PO', 'OSVC'].includes(this.clientsUserType),
            garaged: (!!spz || spz === 0) && !['EV', 'EK'].includes(vehicle.vehicleType),
            expectedKm: !['PV', 'SPS', 'VZV', 'TRA', 'PN', 'EV', 'EK'].includes(
                vehicle.vehicleType,
            ),
            vehicleUse: !!spz || spz === 0,
            withoutRegistration: this.pendingAllowedPromiseDone ?
                this.allowedForWithoutRegistration.some(it => it === this.vehicle.vehicleType) :
                !!vehicle.withoutRegistration,
        };
        if (!this.pendingAllowedPromiseDone) {
            this.pendingAllowedPromise.then(() => {
                const allowed = this.allowedForWithoutRegistration.some(it => it === this.vehicle.vehicleType);
                this.visibilities.withoutRegistration = allowed;
            });
        }

        const keys = ['brand', 'model', 'bodywork', 'fuel', 'expectedKm', 'vehicleUse'];
        const userPartyType = ['OSVC', 'PO'].includes(this.clientsUserType) ? 'IC' : 'RC';
        keys.forEach((key) => {
            if (!this.visibilities[key]) return;

            const params = {
                vehicleType: vehicle.vehicleType,
                brand: vehicle.brand,
                model: vehicle.model,
                userPartyType,
            };

            Object.keys(params).forEach((k) => {
                if (params[k]) return;
                delete params[k];
            });

            this.optionsServ.setOptionsFor(key as any, this.uid, params);
        });
    }

    private vl(fcName: string): any {
        return this.vehicleForm.get(fcName).value;
    }

    private initializeSubs() {
        this.subs.push(
            this.spz.valueChanges.subscribe((val) => {
                if (val === spzVal.have) {
                    this.vehicleForm.get('noLicensePlate').setValue(false, { emitEvent: false });
                    this.vehicleForm.get('notRegistered').setValue(false, { emitEvent: false });
                } else if (val === spzVal.new) {
                    this.vehicleForm.get('noLicensePlate').setValue(true, { emitEvent: false });
                    this.vehicleForm.get('notRegistered').setValue(true, { emitEvent: false });
                } else if (val === spzVal.import) {
                    this.vehicleForm.get('noLicensePlate').setValue(true, { emitEvent: false });
                    this.vehicleForm.get('notRegistered').setValue(false, { emitEvent: false });
                } else {
                    this.vehicleForm.get('noLicensePlate').setValue(true, { emitEvent: false });
                    this.vehicleForm.get('notRegistered').setValue(false, { emitEvent: false });
                }
                if (val !== spzVal.have) this.hideAllAfterSPZ = false;

                this.theseAreUnderMyProtection = [...this._defaultProtections];
                if (this.product === 'FP1')
                    this.theseAreUnderMyProtection.push('clientsVehiclePrice');
                this.nullBelow('licensePlate', this.theseAreUnderMyProtection);
                this.setVisibilities();
            }),
        );
        this.subs.push(
            this.vehicleForm.valueChanges.subscribe(() => {
                this.vehicleChangedEvent.emit();
            }),
        );
        this._htmlData.forEach((htmlDt) => {
            if (htmlDt.name !== 'spz') {
                this.subs.push(
                    this.vehicleForm
                        .get(htmlDt.name)
                        .valueChanges.pipe(debounceTime(200))
                        .subscribe((val) => {
                            if (htmlDt.name === 'licensePlate' && val === this.lastSPZLoaded)
                                return;
                            if (['licensePlate', 'clientsVehiclePrice'].includes(htmlDt.name)) {
                                this.parameterChangedEvent.emit({ key: htmlDt.name, value: val });
                            }
                            if (this.iDareYouIDoubleDareYouMtfk) return;
                            if (!['weight', 'withoutVAT'].includes(htmlDt.name)) {
                                this.nullBelow(
                                    htmlDt.name,
                                    this.product === 'AP1'
                                        ? this.theseAreUnderMyProtection
                                        : [
                                            ...this.theseAreUnderMyProtection,
                                            'clientsVehiclePrice',
                                        ],
                                );
                            }
                            this.setVisibilities();
                        }),
                );
            }
        });
        this.subs.push(
            this.vehicleForm
                .get('vehicleType')
                .valueChanges.pipe(debounceTime(300))
                .subscribe((val) => {
                    this.isElectro = ['EV', 'EK'].includes(val);
                    this.sameAsUserText = this.isElectro
                        ? $localize`Vlastník vozidla`
                        : $localize`Provozovatel vozidla`;
                    this.nullBelow(
                        'vehicleType',
                        this.product === 'AP1' ? null : ['clientsVehiclePrice', 'vIN'],
                    );
                    if (this.iDareYouIDoubleDareYouMtfk) return;
                    this.appServ.startLoading();
                    this.vehicleTypeChanged().then(() => {
                        const fc = this.vehicleForm.get('weight');
                        if (fc.value) {
                            fc.updateValueAndValidity();
                            fc.markAllAsTouched();
                        }
                        this.setVisibilities();
                        this.appServ.stopLoading();
                        this.onGetPriceOrSmth(
                            this.vehicleForm.controls.firstRegistrationDate.value,
                        );
                    });
                }),
        );

        this.subs.push(
            this.vehicleForm
                .get('brand')
                .valueChanges.pipe(debounceTime(300))
                .subscribe(() => {
                    if (this.product !== 'FP1')
                        this.vehicleForm.controls.vIN.setValue(null, { emitEvent: false });
                    this.vehicleForm.controls.technicalCertificateNumber.setValue(null, {
                        emitEvent: false,
                    });
                    this.vehicleForm.controls.numberOfSeats.setValue(null, { emitEvent: false });
                    if (this.iDareYouIDoubleDareYouMtfk) return;
                    this.appServ.startLoading();
                    this.brandChanged().then(() => {
                        this.setVisibilities();
                        this.appServ.stopLoading();
                    });
                }),
        );

        this.subs.push(
            this.vehicleForm
                .get('engine')
                .valueChanges.pipe(debounceTime(300))
                .subscribe(() => {
                    if (this.iDareYouIDoubleDareYouMtfk) return;
                    this.appServ.startLoading();
                    this.engineChanged().then(() => {
                        this.setVisibilities();
                        this.appServ.stopLoading();
                    });
                }),
        );

        this.subs.push(
            this.vehicleForm
                .get('model')
                .valueChanges.pipe(debounceTime(300))
                .subscribe((val) => {
                    if (this.product !== 'FP1')
                        this.vehicleForm.controls.vIN.setValue(null, { emitEvent: false });
                    this.vehicleForm.controls.technicalCertificateNumber.setValue(null, {
                        emitEvent: false,
                    });
                    this.vehicleForm.controls.numberOfSeats.setValue(null, { emitEvent: false });
                    const fromNull = !this.oldModel && !!val;
                    this.oldModel = val;
                    if (this.iDareYouIDoubleDareYouMtfk) return;
                    this.appServ.startLoading();
                    this.modelChanged().then(() => {
                        if (this.vehicleForm.controls.engineVolume.value && !fromNull)
                            this.youShouldIdontKnowGetPriceOrSomething();
                        this.setVisibilities();
                        this.appServ.stopLoading();
                    });
                }),
        );

        // this.subs.push(this.vehicleForm.get('modelYear').valueChanges.pipe(debounceTime(300)).subscribe(() => {
        //   if (this.iDareYouIDoubleDareYouMtfk) return;
        //   this.appServ.startLoading();
        //   this.modelYearChanged().then(() => {
        //     this.setVisibilities();
        //     this.appServ.stopLoading();
        //   });
        // }));
        this.subs.push(
            this.vehicleForm.get('licensePlate').valueChanges.subscribe((val) => {
                if (!val) this.lastSPZLoaded = null;
                if (this.iDareYouIDoubleDareYouMtfk) return;
                if (val !== this.lastSPZLoaded) this.hideAllAfterSPZ = true;
            }),
        );

        this.subs.push(
            this.sameAsUserFc.valueChanges.subscribe((val) => {
                if (this.iDareYouIDoubleDareYouMtfk) return;
                if (['EV', 'EK'].includes(this.vehicleForm.controls.vehicleType.value)) {
                    this.policyHolderSameAsVehicleOwnerEvent.emit(val);
                } else {
                    this.policyHolderSameAsVehicleHolderEvent.emit(val);
                }
                this.valid = true;
                this.changedEvent.emit();
            }),
        );
        this.subs.push(
            this.vehicleForm.valueChanges.subscribe(() => {
                if (this.iDareYouIDoubleDareYouMtfk) return;
                if (!this.markTouchedBuVisible) this.markTouchedBuVisible = true;
                // this.removeValueNotInOptions();
                if (this.valid !== this.vehicleForm.valid) {
                    if (this.vehicleForm.valid && this.hideAllAfterSPZ) return;
                    this.valid = this.vehicleForm.valid;
                    this.validityChangedEvent.emit(this.valid);
                }
                this.changedEvent.emit();
            }),
        );

        this.subs.push(
            this.vehicleForm.controls.noTechnicalCertificate.valueChanges.subscribe((tp) => {
                if (tp) {
                    this.vehicleForm.controls.technicalCertificateNumber.disable();
                    this.vehicleForm.controls.technicalCertificateNumber.setValue(null);
                } else {
                    this.vehicleForm.controls.technicalCertificateNumber.enable();
                }
            }),
        );

        this.listOfObserved?.forEach((key) => {
            this.subs.push(
                this.vehicleForm.get(key).valueChanges.subscribe((value) => {
                    this.observedChangedEvent.emit({ key, value });
                }),
            );
        });
    }

    // private removeValueNotInOptions(): void {
    //   Object.keys(this.options).forEach(key => {
    //     const fc = this.vehicleForm.controls[key];
    //     const visible = this.visibilities[key];
    //     if (visible && fc && fc.value && !this.options[key].some(it => it.value === fc.value)) {
    //       fc.setValue(null);
    //     }
    //   });
    // }

    protected onGetPriceOrSmth(firstRegistrationDate: TDate) {
        if (this.iDareYouIDoubleDareYouMtfk) return;
        if (
            this.isAllBeforeFcValid('firstRegistrationDate') &&
            firstRegistrationDate &&
            this.vehicleForm.get('firstRegistrationDate').valid
        ) {
            this.youShouldIdontKnowGetPriceOrSomething();
        }
    }

    private isAllBeforeFcValid(fcName: string): boolean {
        for (const key of Object.keys(this.vehicleForm.controls)) {
            if (key === fcName) return true;
            if (this.visibilities[key] && !this.vehicleForm.controls[key].valid) return false;
        }
        return true;
    }

    private vehicleTypeChanged() {
        const vehicleType = this.vehicleForm.get('vehicleType').value;
        return this.optionsServ.setOptionsFor('brand', this.uid, { vehicleType });
    }

    private brandChanged() {
        const cnt = this.vehicleForm.controls;
        if (cnt.vehicleType.value === 'OA' && cnt.vehicleName.value) {
            cnt.vehicleName.setValue(null);
        }
        const brand = this.vehicleForm.get('brand').value;
        const model = this.vehicleForm.get('model').value;
        const modelYear = this.vehicleForm.get('modelYear').value;
        const engine = this.vehicleForm.get('engine').value;

        return Promise.all([
            this.optionsServ.setOptionsFor('model', this.uid, { brand }),
            this.optionsServ.setOptionsFor('modelYear', this.uid, { brand, model }),
            this.optionsServ.setOptionsFor('engine', this.uid, { brand, model, modelYear }),
            this.optionsServ.setOptionsFor('bodywork', this.uid, {
                brand,
                model,
                modelYear,
                engine,
            }),
        ]);
    }

    private modelChanged() {
        const cnt = this.vehicleForm.controls;
        if (cnt.vehicleType.value === 'OA' && cnt.vehicleName.value) {
            cnt.vehicleName.setValue(null);
        }
        const brand = this.vehicleForm.get('brand').value;
        const model = this.vehicleForm.get('model').value;
        const modelYear = this.vehicleForm.get('modelYear').value;
        const engine = this.vehicleForm.get('engine').value;

        return Promise.all([
            this.optionsServ.setOptionsFor('modelYear', this.uid, { brand, model }),
            this.optionsServ.setOptionsFor('engine', this.uid, { brand, model, modelYear }),
            this.optionsServ.setOptionsFor('bodywork', this.uid, {
                brand,
                model,
                modelYear,
                engine,
            }),
        ]);
    }

    // private modelYearChanged() {
    //   const brand = this.vehicleForm.get('brand').value;
    //   const model = this.vehicleForm.get('model').value;
    //   const modelYear = this.vehicleForm.get('modelYear').value;
    //   const engine = this.vehicleForm.get('engine').value;

    //   return Promise.all([
    //     this.optionsServ.setOptionsFor('engine', this.uid, { brand, model, modelYear }),
    //     this.optionsServ.setOptionsFor('bodywork', this.uid, { brand, model, modelYear, engine }),
    //   ]);
    // }

    private engineChanged() {
        const brand = this.vehicleForm.get('brand').value;
        const model = this.vehicleForm.get('model').value;
        // const modelYear = this.vehicleForm.get('modelYear').value;
        const engine = this.vehicleForm.get('engine').value;

        return this.optionsServ.setOptionsFor('bodywork', this.uid, { brand, model, engine });
    }

    private nullBelow(fcName: string, keepUnchanged?: string[]) {
        keepUnchanged = keepUnchanged ? keepUnchanged : [];
        let list = Object.keys(this.vehicleForm.controls);
        list = list.slice(list.indexOf(fcName) + 1);
        list.forEach((key) => {
            if (keepUnchanged.includes(key)) return;
            const fc = this.vehicleForm.controls[key];
            const newVl = this.dataServ.defaultData[key];
            if (fc === newVl) return;
            // if (this.product === 'AP1') fc.setValue(newVl, { emitEvent: false });
            // else
            if (!fc.valid) fc.setValue(newVl, { emitEvent: false });
        });
        this.setVisibilities();
    }

    private launchChaines(
        data: { fcName: string; controls: string[] },
        vehicle: TsXmlParAp1Vehicle,
    ) {
        return this.chaines(data.controls.slice(data.controls.indexOf(data.fcName)), vehicle);
    }

    private chaines(list: string[], vehicle: TsXmlParAp1Vehicle) {
        if (!list.length) {
            return Promise.resolve();
        }
        const current = list[0];
        const unknownOption =
            this.options[current] &&
            !this.options[current].some((op) => op.value === vehicle[current]);
        if ((!vehicle[current] || unknownOption) && current !== 'vehicleName')
            return this.chaines(list.slice(1), vehicle);
        this.vehicleForm.get(current).setValue(vehicle[current], { emitEvent: false });
        if (this[current + 'Changed']) {
            return this[current + 'Changed']().then(() => {
                return this.chaines(list.slice(1), vehicle);
            });
        } else {
            return this.chaines(list.slice(1), vehicle);
        }
    }

    protected onFocus(el: any, type: string) {
        if (['select', 'date'].includes(type)) el.open();
        else if (['input', 'number'].includes(type)) el.focus();
    }

    protected onBlur(fc: string) {
        this.takeCareOfTrim(fc);
        if (fc === 'licensePlate') {
            const ctrl = this.vehicleForm.get(fc);
            if (!ctrl.valid || !this.hideAllAfterSPZ || ctrl.value === this.lastSPZLoaded) return;

            ctrl.setValue(ctrl.value.toUpperCase(), { emitEvent: false });
            this.appServ.startLoading();
            this.optionsServ
                .loadVehicle(ctrl.value, this.uid)
                .then((vehicle) => {
                    this.lastSPZLoaded = vehicle.licensePlate;
                    this.theseAreUnderMyProtection = vehicle.weight
                        ? [...this._defaultProtections, 'weight']
                        : [...this._defaultProtections];
                    if (vehicle.vehicleName) this.theseAreUnderMyProtection.push('vehicleName');
                    if (this.product === 'FP1')
                        this.theseAreUnderMyProtection.push(
                            'garaged',
                            'withoutVAT',
                            'clientsVehiclePrice',
                        );
                    this.setLoadedVehicle(vehicle);
                    this.setVisibFromVehicle(vehicle);
                })
                .catch(() => {
                    this.hideAllAfterSPZ = false;
                    this.setVisibilities();
                })
                .finally(() => {
                    this.hideAllAfterSPZ = false;
                    this.appServ.stopLoading();
                });
        } else if (fc === 'weight') {
            if (this.iDareYouIDoubleDareYouMtfk) return;
            if (this.isAllBeforeFcValid('weight') && this.spz.value === 1) {
                this.youShouldIdontKnowGetPriceOrSomething();
            }
        } else if (fc === 'productionYear') {
            if (this.iDareYouIDoubleDareYouMtfk) return;
            if (this.isAllBeforeFcValid('productionYear') && this.spz.value) {
                this.youShouldIdontKnowGetPriceOrSomething();
            }
        }
    }

    private takeCareOfTrim(key: string): void {
        if (
            ![
                'licensePlate',
                'vehicleName',
                'productionYear',
                'vIN',
                'technicalCertificateNumber',
            ].includes(key)
        )
            return;
        const fc = this.vehicleForm.controls[key];
        const trimmedVal = fc.value ? fc.value.trim() : fc.value;
        if (fc.value !== trimmedVal) {
            fc.setValue(trimmedVal, { emitEvent: false });
        }
    }

    private youShouldIdontKnowGetPriceOrSomething() {
        if (
            this.isAllBeforeFcValid('firstRegistrationDate') &&
            this.vehicleForm.get('firstRegistrationDate').valid &&
            !this.isElectro
        ) {
            console.log(this.vehicleForm.controls)
            this.getRealOutput(this.vehicleForm.value).then((vehicle) => {
                if (!vehicle.weight) vehicle.weight = 5;
                this.rateEvent.emit(vehicle);
            });
        }
    }

    private setLoadedVehicle(vehicle: TsXmlParAp1Vehicle) {
        this.appServ.startLoading();

        const controls = Object.keys(this.vehicleForm.controls).filter((fc) =>
            Object.keys(vehicle).includes(fc),
        );
        this.iDareYouIDoubleDareYouMtfk = true;
        this.launchChaines({ fcName: 'vehicleType', controls }, vehicle)
            .then(() => {
                this.setVisibilities();
                setTimeout(() => {
                    if (vehicle.firstRegistrationDate) this.youShouldIdontKnowGetPriceOrSomething();
                    this.setVisibilities();
                });
                if (this.valid !== this.vehicleForm.valid) {
                    this.valid = this.vehicleForm.valid;
                }
            })
            .finally(() => {
                setTimeout(() => {
                    this.iDareYouIDoubleDareYouMtfk = false;
                    this.appServ.stopLoading();
                }, 501);
            });
    }

    private async save2Service() {
        if (this.product === 'FP1') return;
        const data = await this.prepareData();
        data.vehicleValue.productionYear = this.vehicleForm.get('productionYear').value;
        const theseAreUnderMyProtection = this.theseAreUnderMyProtection;
        this.dataServ.savedData = { ...data, spz: this.spz.value, theseAreUnderMyProtection };
    }

    private async prepareData(final?: boolean): Promise<{
        sameAsUser: boolean;
        vehicleValue: any;
        vehicleUseTxt: string;
    }> {
        const vehicleUseTxt =
            this.options?.vehicleUse?.find(
                (it) => it.value === this.vehicleForm.controls.vehicleUse.value,
            )?.text || '';
        const data = {
            sameAsUser: this.sameAsUserFc.value,
            vehicleValue: final
                ? await this.getRealOutput(this.vehicleForm.value)
                : this.vehicleForm.value,
            vehicleUseTxt,
        };
        return data;
    }

    private async getRealOutput(vehicleValue: any) {
        // this.setVisibilities();
        if (!this.visibilities.brand) vehicleValue.brand = 'NEUVEDENA';
        if (!this.visibilities.model) {
            vehicleValue.model =
                vehicleValue.vehicleType === 'OA' && vehicleValue.brand === 'OSTATNE'
                    ? '579'
                    : '529';
        }
        if (!this.visibilities.withoutVAT) vehicleValue.withoutVAT = false;
        if (!this.visibilities.expectedKm) vehicleValue.expectedKm = -1;
        const inPVPN = ['PV', 'PN'].includes(vehicleValue.vehicleType);
        if (!this.visibilities.fuel) {
            if (inPVPN) {
                vehicleValue.fuel = 'JI';
            } else if (this.isElectro) {
                vehicleValue.fuel = 'EP';
            } else {
                vehicleValue.fuel = await this.optionsServ.getVehicleFuel(
                    {
                        brand: this.vehicleForm.get('brand').value,
                        model: this.vehicleForm.get('model').value,
                        modelYear: this.vehicleForm.get('modelYear').value,
                    },
                    this.uid,
                );
            }
        }
        let engineVal: { volume: number; power: number };
        if (
            !inPVPN &&
            (!this.visibilities.engineVolume || !this.visibilities.enginePower) &&
            this.visibilities.engine
        ) {
            engineVal = await this.optionsServ.getEngineValues(
                {
                    brand: this.vehicleForm.get('brand').value,
                    model: this.vehicleForm.get('model').value,
                    modelYear: this.vehicleForm.get('modelYear').value,
                    engine: this.vehicleForm.get('engine').value,
                },
                this.uid,
            );
        }
        if (!this.visibilities.engineVolume) {
            if (inPVPN || vehicleValue.fuel === 'EP') {
                vehicleValue.engineVolume = 0;
            } else {
                vehicleValue.engineVolume = engineVal?.volume;
            }
        }
        if (!this.visibilities.enginePower) {
            if (inPVPN) {
                vehicleValue.enginePower = 0;
            } else {
                vehicleValue.enginePower = engineVal?.power;
            }
        }
        if (!this.visibilities.numberOfSeats) {
            if (inPVPN || this.isElectro) {
                vehicleValue.numberOfSeats = 0;
            } else {
                vehicleValue.numberOfSeats = await this.optionsServ.getNumberOfSeats(
                    {
                        brand: this.vehicleForm.controls.brand.value,
                        model: this.vehicleForm.controls.model.value,
                        modelYear: this.vehicleForm.controls.modelYear.value,
                        // engine: this.vehicleForm.controls.engine.value,
                        bodywork: this.vehicleForm.controls.bodywork.value,
                    },
                    this.uid,
                );
            }
        }
        if (vehicleValue.clientsVehiclePrice || vehicleValue.clientsVehiclePrice === 0) {
            vehicleValue.clientsVehiclePrice = String(vehicleValue.clientsVehiclePrice);
        }
        if (!vehicleValue.equipment) vehicleValue.equipment = 'NA';
        if (
            !vehicleValue.vehicleName &&
            this.options?.brand?.length &&
            this.visibilities.model &&
            this.options.model?.length
        ) {
            const brandTxt = this.options.brand.find(
                (br) => br.value === this.vehicleForm.get('brand').value,
            )?.text;
            const modelTxt = this.options.model.find(
                (br) => br.value === this.vehicleForm.get('model').value,
            )?.text;
            vehicleValue.vehicleName = `${brandTxt} ${modelTxt}`;
        }

        vehicleValue = this.unOstatni(vehicleValue);

        if (!this.dataServ.savedData) {
            const theseAreUnderMyProtection = this.theseAreUnderMyProtection;
            this.dataServ.savedData = {
                sameAsUser: this.sameAsUserFc.value,
                spz: this.spz.value,
                theseAreUnderMyProtection,
                vehicleValue: null,
            };
        }
        this.dataServ.savedData.vehicleValue = vehicleValue;
        if (vehicleValue.productionYear)
            vehicleValue.productionYear = new TDate(new Date(String(vehicleValue.productionYear)));
        vehicleValue.withoutVAT = this.vehicleForm.controls.withoutVAT.value || false;
        if (this.isElectro) vehicleValue.garaged = false;
        return vehicleValue;
    }

    private unOstatni(vehicle: TsXmlParAp1Vehicle): TsXmlParAp1Vehicle {
        if (vehicle.modelYear === 'Ostatní')
            // TODO
            vehicle.modelYear = null;
        if (vehicle.engine === 'Ostatní') vehicle.engine = null;
        if (vehicle.bodywork === 'Ostatní') vehicle.bodywork = null;
        return vehicle;
    }

    protected _htmlFunIsFCInvalid(fcName: string): boolean {
        const fc = fcName === 'spz' ? this.spz : this.vehicleForm.get(fcName);
        return !fc.disabled && fc.touched && !fc.valid;
    }

    private done() {
        if (this.vehicleForm.valid) {
            this.prepareData(true).then((data) => {
                this.doneEvent.emit(data);
            });
        } else {
            this.vehicleForm.markAllAsTouched();
            setTimeout(() => {
                const el = document.querySelector('.form-group--error');
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el?.querySelector('input')?.focus();
            }, 100);
        }
    }

    protected onFinishHim() {
        if (!this.vehicleForm.valid) {
            Object.keys(this.vehicleForm.controls).forEach((key) => {
                if (!this.vehicleForm.controls[key].valid && !this.visibilities[key]) {
                    this.vehicleForm.controls[key].setErrors(null, { emitEvent: false });
                    this.vehicleForm.controls[key].setValue(null, { emitEvent: false });
                }
            });
        }
        if (this.vehicleForm.valid) {
            if (this.cookieServ.get('campaign')) this.cookiesServ.sendAdForm('Dum spocitat');
            this.done();
        } else {
            if (!this.hideAllAfterSPZ) {
                this.vehicleForm.markAllAsTouched();
                setTimeout(() => {
                    const el = document.querySelector('.form-group--error');
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el?.querySelector('input')?.focus();
                }, 100);
            }
        }
    }

    protected onShowTooltip(name: string): void {
        if (!name || !this.tooltips[name]) return;
        this.ouhaServ.tooltip(this.tooltips[name]);
    }

    public setPrice(price: boolean) {
        if (this.vehicleForm.get('vehiclePrice').value !== price)
            this.vehicleForm.get('vehiclePrice').setValue(price, { emitEvent: false });
        if (price) {
            this.vehicleForm.get('clientsVehiclePrice').setValue(null, { emitEvent: false });
            this.vehicleForm.get('clientsVehiclePrice').setErrors(null, { emitEvent: false });
        }
        const _240Mnth = this.othersServ.getDateChangedBy(this.now, 0, -240);
        let age = this.vl('productionYear') || this.vl('firstRegistrationDate');
        if (typeof age === 'string') age = new Date(age);
        const tooOld = age && age.getTime() < _240Mnth.getTime();
        const visible =
            !tooOld && !this.vl('vehiclePrice') && !['EV', 'EK'].includes(this.vl('vehicleType'));
        this.visibilities.clientsVehiclePrice = visible && !price;
    }

    public getVehicle(): TsXmlParAp1Vehicle {
        if (this.product === 'FP1') return { ...this.vehicleForm.getRawValue() } as any;
        const vehicle = this.dataServ?.savedData?.vehicleValue ?? this.vehicleForm.value;
        return { ...vehicle };
    }

    public getReadVehicle(): Promise<TsXmlParAp1Vehicle> {
        return this.getRealOutput(this.vehicleForm.value);
    }

    public validate(): boolean {
        this.vehicleForm.markAllAsTouched();
        return this.vehicleForm.valid;
    }

    public isValid(): boolean {
        return this.vehicleForm.valid;
    }

    public patchVehicle(
        vehicle: {
            bodywork?: string;
            brand?: string;
            brandModelCode?: string;
            clientsVehiclePrice?: string;
            contractualService?: boolean;
            engine?: string;
            enginePower?: number;
            engineVolume?: number;
            equipment?: string;
            expectedKm?: number;
            firstRegistrationDate?: TDate;
            fuel?: string;
            garaged?: boolean;
            licensePlate?: string;
            model?: string;
            modelYear?: string;
            noLicensePlate?: boolean;
            noTechnicalCertificate?: boolean;
            notRegistered?: boolean;
            numberOfSeats?: number;
            productionYear?: TDate;
            technicalCertificateNumber?: string;
            vehicleName?: string;
            vehiclePrice?: boolean;
            vehicleType?: string;
            vehicleUse?: string;
            vIN?: string;
            weight?: number;
            withoutVAT?: boolean;
            x4x4?: boolean;
        },
        emitEvent?: boolean,
    ): void {
        if (!vehicle || typeof vehicle !== 'object' || !Object.keys(vehicle).length)
            throw new Error('Wrong input!');
        this.vehicleForm.patchValue(vehicle, {
            emitEvent: typeof emitEvent === 'boolean' ? emitEvent : true,
        });
    }

    protected _htmlFunZipBlur() {
        setTimeout(() => {
            const value = this.zipCodeCity.value;
            if (value && typeof value === 'string') {
                const vl = value as string;
                this.isLoadingZip = true;
                this.filterZip(vl)
                    .then(() => {
                        if (this.zips?.length) {
                            let _dt;
                            if (vl.includes('-'))
                                _dt = this.zips.find(
                                    (dt) =>
                                        dt.city.toLowerCase() === vl.split('-')[1]?.toLowerCase(),
                                );
                            if (!_dt) _dt = this.zips[0];
                            this.zipCodeCity.setValue(_dt);
                        }
                    })
                    .finally(() => {
                        this.isLoadingZip = false;
                    });
            }
        }, 300);
    }

    private filterZip(value: string): Promise<TsXmlCodelistEntry[]> {
        return new Promise((resolve, reject) => {
            if (typeof value !== 'string') {
                resolve(null);
                return;
            }

            let filterValue: string;

            filterValue = value?.replace(/\s/g, '');
            if (filterValue?.length > 2) {
                if (filterValue.length > 5) {
                    filterValue = filterValue.substring(0, 5);
                }

                this.restServ
                    .myParCodelistsEntriesByColumnsPost(
                        '5116',
                        [{ code: 'PSC', value: filterValue }],
                        this.uid,
                    )
                    .then((entries: TsXmlCodelistEntries) => {
                        if (entries?.entry?.length) {
                            const entriesSorted = entries.entry.sort((val1, val2) => {
                                return val1.gclColumn
                                    .find((vl) => vl.columnCode === 'OBEC_NAZEV')
                                    .stringValue.localeCompare(
                                        val2.gclColumn.find((vl) => vl.columnCode === 'OBEC_NAZEV')
                                            .stringValue,
                                    );
                            });
                            resolve(entriesSorted);
                            this.zips = [];
                            entriesSorted.forEach((ent) => {
                                const zipCode = ent.gclColumn.find(
                                    (cl) => cl.columnCode === 'PSC',
                                )?.stringValue;
                                const city = ent.gclColumn.find(
                                    (cl) => cl.columnCode === 'OBEC_NAZEV',
                                )?.stringValue;
                                const municipalityCode = ent.gclColumn.find(
                                    (cl) => cl.columnCode === 'OBEC_KOD',
                                )?.stringValue;
                                this.zips.push({ zipCode, city, municipalityCode });
                            });
                        } else {
                            resolve([]);
                        }
                    })
                    .catch((e) => {
                        reject(e);
                    });
            } else {
                resolve(null);
            }
        });
    }

    protected _htmlFunDisplayFn(item: {
        zipCode: string;
        city: string;
        municipalityCode: string;
    }): string {
        if (!item) return '';
        return `${item.zipCode}-${item.city}`;
    }

    protected displ(ev): string {
        return ev?.municipality ? `${ev.zipCode}-${ev.city}` : '';
    }

    protected onBlurOnDate(fcN: string): void {
        const fc = this.vehicleForm.controls[fcN];
        this.trimServ.trim(fc);
        this.onGetPriceOrSmth(fc);
    }

    public unvalide(): void {
        this.valid = false;
    }

    ngOnDestroy() {
        this.subs?.forEach((sub) => sub?.unsubscribe());
        this.step3Serv.vehicleUse =
            this.options?.vehicleUse?.find(
                (op) => op.value === this.vehicleForm.get('vehicleUse').value,
            )?.text || null;
        this.save2Service();
    }
}
